<!--
  CategoryCard.vue -- C2 (category cards + core fields).

  Card: Store Editor: Categories & Storefront page (ADR 0059)
  https://trello.com/c/a7j1VV6D
  Pebble: C2 -- Category cards + core fields (stacked cards, Home Page
  special-casing, UrlBar + copy-link, rich-text editor, Dropzone banner,
  visibility toggle).

  Design: planning/design-handoffs/handoff_2026-07-23/
          2026-07-23-store-categories/design/cats-store.jsx (CategoryCard,
          UrlBar, NameField, RichText, Banner, VisibilityToggle,
          ProductList) + Locked Decisions.md. Recreated with Vuetify 3 +
          the app's existing RichTextEditor/ImageUploader components -- NOT the
          raw HTML from the design reference.

  This is a pure, CONTROLLED presentational component -- it never calls
  the API itself. The parent (StoreCategories/index.vue) owns every PATCH/
  POST to B1 (api/src/endpoints/store_categories.py) and passes the
  category row + a few display-only props down. On a user edit this card
  emits an intent event; the parent decides how/when to persist (debounce
  for text fields, immediate PATCH for toggles) -- mirrors the contract
  ImageUploader.vue already established (emit upload/remove, parent
  persists).

  Home category special-casing (Locked Decisions.md): pinned first
  (parent's job to order), pin icon not a drag handle, no delete/hide
  affordance, URL bar = storefront root. Its NAME, however, IS editable
  (same name input the non-home cards use) -- Jake reversed the original
  "name fixed" Locked Decision on 2026-07-29: the home category's display
  name is the storefront nav "Home" label and is managed on this page. The
  slug stays the storefront root ("") regardless of the name; home is
  always identified by is_home, never by its name. It is otherwise a FULL
  card -- banner + rich text + curated products, same as any other
  category.

  Product list (C3): a responsive auto-fill grid, drag-reorderable via
  sortablejs. MEMBERSHIP stays read-only (assignment happens in the
  Product editor, enforced server-side by the product-order endpoint --
  see store_categories.py::patch_category_product_order) -- this card
  only reorders. On drop it emits `update-product-order(categoryId,
  orderedCampaignProductIds)`; the parent PATCHes
  /api/stores/<id>/categories/<cid>/product-order with EXACTLY
  {"product_order": [{"campaign_product_id", "sort_order"}, ...]}. The
  card never calls the API itself (same controlled-component contract as
  every other field).

  ADOPTED products (2026-08-26): the Home category also lists every
  product in the store with NO category at all -- the server flags those
  `adopted: true` with `sort_order: null` (store_categories.py
  ::_serialize_category_with_products).

  They are FULLY DRAGGABLE, same as any assigned product (Jake,
  2026-08-26: "I should be able to reorder products in the home category
  (i.e. unassigned) just like in the other categories"). They did not
  start that way: PR #3004 rendered them read-only -- lock glyph, no
  drag -- because the product-order endpoint rejected the WHOLE request
  for any id with no membership row, so emitting an adopted id would
  have 400'd every Home reorder including the real members'. That was
  correct for the contract at the time. The contract changed first: on
  HOME, patch_category_product_order now MATERIALISES a membership row
  for a product that is uncategorized in this store, which is what gives
  the drag somewhere to persist a sort_order.

  Both of #3004's guards came off together -- the sortablejs `filter`
  entry AND the `onProductReorder` filtering -- because `filter` only
  blocks a row from STARTING a drag, not from being displaced by one.
  Removing one without the other leaves a half-working drag.

  The "Uncategorised" pill STAYS. It is a status, not a restriction: the
  product genuinely has no category until it is dragged (the products
  table says the same word for the same rows), and dragging it is what
  assigns it to Home.

  CC1 (single-open accordion, ADR 0059 2026-07-27 revision + Locked
  Decisions.md "Layout"): the card is collapsed by default. `expanded` is
  a PROP -- the parent (StoreCategories/index.vue) owns which single card
  is open and passes it down; this card never decides its own open state,
  same controlled-component contract as every field. Clicking the
  collapsed header row (or the explicit expand/collapse chevron) emits
  `toggle-expand(categoryId)`; the parent flips its single openCategoryId.
  The body renders with v-show (not v-if) when collapsed, NOT unmounted --
  this keeps the rich-text editor/ImageUploader alive so the existing
  field-level event contracts keep working whenever a test or the parent
  opens the card, and avoids an expensive editor remount on every
  expand/collapse.

  Test-id prefix: ct-categories-* (per-field ids carry the category id so
  multiple cards on one page stay distinct, e.g.
  ct-categories-name-input-<id>).
-->
<template>
  <div
    class="category-card"
    :class="{
      'category-card--new': isNew,
      'category-card--hidden': category.hidden,
    }"
    :data-testid="`ct-categories-card-${category.id}`"
  >
    <!-- ============================================================
         Header -- always visible; clicking it (outside the
         visibility/delete buttons) toggles expand/collapse.
         ============================================================ -->
    <div
      class="category-card__header"
      :class="{ 'category-card__header--expanded': expanded }"
      :data-testid="`ct-categories-header-click-${category.id}`"
      role="button"
      tabindex="0"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="onToggleExpand"
      @keydown.enter.prevent="onToggleExpand"
      @keydown.space.prevent="onToggleExpand"
    >
      <span
        v-if="category.is_home"
        class="tf-icon category-card__pin"
        :data-testid="`ct-categories-pin-${category.id}`"
        title="Pinned first"
        aria-label="Pinned first"
        >push_pin</span
      >
      <span
        v-else
        class="tf-icon category-card__drag"
        :data-testid="`ct-categories-drag-${category.id}`"
        title="Drag to reorder"
        aria-label="Drag to reorder"
        @click.stop
        >drag_indicator</span
      >

      <div class="category-card__heading">
        <div class="category-card__title-row">
          <span class="category-card__title">{{ category.name || "Untitled category" }}</span>
          <span v-if="category.is_home" class="category-card__pill" data-testid="ct-categories-home-pill">
            <span class="tf-icon">home</span>Home page
          </span>
          <span
            v-if="category.hidden"
            class="category-card__pill category-card__pill--hidden"
            data-testid="ct-categories-hidden-pill"
          >
            <span class="tf-icon">visibility_off</span>Hidden from nav
          </span>
          <span v-if="isNew" class="category-card__pill category-card__pill--new" data-testid="ct-categories-new-pill">
            <span class="tf-icon">fiber_new</span>New &mdash; unsaved
          </span>
        </div>
        <div v-if="category.is_home" class="category-card__subtext">
          Always first &middot; can't be deleted or hidden
        </div>
      </div>

      <div class="category-card__spacer" />

      <span
        class="category-card__count-chip"
        :data-testid="`ct-categories-count-chip-${category.id}`"
      >{{ countLabel }}</span>

      <button
        v-if="!category.is_home"
        type="button"
        class="category-card__visibility-toggle"
        :class="{ 'category-card__visibility-toggle--hidden': category.hidden }"
        :data-testid="`ct-categories-visibility-${category.id}`"
        :title="category.hidden ? 'Hidden from storefront nav -- click to show' : 'Shown in storefront nav -- click to hide'"
        @click.stop="onToggleVisibility"
      >
        <span class="tf-icon">{{ category.hidden ? "visibility_off" : "visibility" }}</span>
        {{ category.hidden ? "Hidden" : "Visible" }}
      </button>

      <button
        v-if="!category.is_home"
        type="button"
        class="category-card__delete-btn"
        :data-testid="`ct-categories-delete-${category.id}`"
        title="Delete"
        @click.stop="$emit('delete-category', category.id)"
      >
        <span class="tf-icon">delete_outline</span>Delete
      </button>

      <button
        type="button"
        class="category-card__expand-btn"
        :data-testid="`ct-categories-expand-${category.id}`"
        :title="expanded ? 'Collapse' : 'Expand'"
        :aria-label="expanded ? 'Collapse category' : 'Expand category'"
        @click.stop="onToggleExpand"
      >
        <span class="tf-icon">{{ expanded ? "expand_less" : "expand_more" }}</span>
      </button>
    </div>

    <!-- ============================================================
         Body -- v-show (not v-if): stays mounted while collapsed so
         the rich-text editor/ImageUploader never remount on every
         expand/collapse and controlled-component event contracts keep working
         regardless of the visual collapse state.
         ============================================================ -->
    <div
      v-show="expanded"
      class="category-card__body"
      :data-testid="`ct-categories-body-${category.id}`"
    >
      <!-- The is_home card's NAME is editable too (Jake reversed the
           "name fixed" Locked Decision 2026-07-29): the home category's
           display name IS the storefront nav "Home" label, managed here.
           Its slug stays the storefront root regardless of the name, so
           the "storefront link is generated from this name" hint is only
           shown for non-home cards. -->
      <div class="category-card__section">
        <label class="category-card__field-label" :for="`ct-categories-name-input-${category.id}`">Category name *</label>
        <input
          :id="`ct-categories-name-input-${category.id}`"
          ref="nameInput"
          type="text"
          class="category-card__name-input"
          :data-testid="`ct-categories-name-input-${category.id}`"
          :value="category.name"
          :disabled="!editMode"
          placeholder="Untitled category"
          @blur="onNameBlur"
          @keydown.enter="($event.target).blur()"
        />
        <p v-if="isNew && !category.is_home" class="category-card__hint">The storefront link is generated from this name.</p>
      </div>

      <div class="category-card__section">
        <div v-if="!category.is_home" class="category-card__section-label">Storefront link</div>
        <div
          class="category-card__urlbar"
          :class="{ 'category-card__urlbar--hidden': emphUrlBar }"
          :data-testid="`ct-categories-urlbar-${category.id}`"
        >
          <span class="tf-icon category-card__urlbar-icon">{{ urlBarIcon }}</span>
          <div class="category-card__urlbar-body">
            <div class="category-card__urlbar-label">{{ urlBarLabel }}</div>
            <span v-if="isUnset" class="category-card__urlbar-unset">Set a name to generate the link</span>
            <a
              v-else
              :href="storefrontUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="category-card__urlbar-path"
              :data-testid="`ct-categories-urlbar-link-${category.id}`"
              title="Open on the storefront (new tab)"
            >{{ storefrontUrl }}</a>
          </div>
          <span v-if="!category.is_home && !isUnset" class="category-card__urlbar-lock">
            <span class="tf-icon">lock</span>slug auto-set
          </span>
          <button
            v-if="!isUnset"
            type="button"
            class="category-card__copy-btn"
            :data-testid="`ct-categories-copy-link-${category.id}`"
            title="Copy link"
            @click="onCopyLink"
          >
            <span class="tf-icon">content_copy</span>Copy link
          </button>
        </div>
      </div>

      <!-- Banner sits ABOVE the category text (Locked Decisions.md
           "Layout", revised 2026-07-27) -- the dropzone is wrapped in
           category-card__banner-tight to reduce ImageUploader's default
           min-height/padding (see <style scoped>) without forking the
           shared component. -->
      <div class="category-card__section">
        <div class="category-card__section-label">Banner image</div>
        <!-- Recommended banner size. Derived from how the storefront
             renders the banner, not invented: the numbers come from
             utils/categoryBannerLayout.js. Since GH-3633 the storefront
             renders the banner full-bleed, like the home banner (shared
             StorefrontBanner), so the size is 2x a 1440px desktop at 6:1. -->
        <p
          class="category-card__banner-guidance"
          :data-testid="`ct-categories-banner-size-guidance-${category.id}`"
        >
          Recommended size: <strong>{{ bannerSizeLabel }}</strong>, a wide,
          short strip. It runs the full width of the page, like the banner on
          your home page. Other shapes still work -- very tall images show
          space at the sides -- and a banner is never cropped.
        </p>
        <div class="category-card__banner-tight">
          <image-uploader
            :testid-prefix="`ct-categories-banner-${category.id}`"
            :current-image="bannerUrl"
            :disabled="!editMode"
            :uploading="bannerUploading"
            title="Upload a banner"
            hint="JPG or PNG, up to 5 MB."
            @upload="onBannerUpload"
            @remove="onBannerRemove"
            @error="onBannerError"
          />
        </div>
      </div>

      <div class="category-card__section">
        <div class="category-card__section-label">Category text</div>
        <div class="category-card__rte" :data-testid="`ct-categories-rte-wrap-${category.id}`">
          <rich-text-editor
            :model-value="category.rich_text || ''"
            :read-only="!editMode"
            :data-testid="`ct-categories-rte-${category.id}`"
            @update:model-value="onRichTextChange"
          />
        </div>
      </div>

      <div class="category-card__section">
        <div class="category-card__products-header">
          <span class="category-card__section-label">
            {{ category.is_home ? "Products on the home page" : "Assigned products" }}
          </span>
          <span class="category-card__products-count">
            &middot; {{ products.length }}<template v-if="hiddenProductCount > 0"> &middot; {{ hiddenProductCount }} hidden</template>
          </span>
          <div class="category-card__spacer" />
          <span class="category-card__lock-hint">
            <span class="tf-icon">lock</span>Assigned in the Product editor
          </span>
        </div>
        <p v-if="products.length > 0" class="category-card__drag-hint">
          <span class="tf-icon">drag_indicator</span>
          Drag to set the order shoppers see &mdash; left to right, then top to bottom.
        </p>
        <!-- Home only: explain WHY rows nobody assigned are on this list,
             and what the two ways off it are. The copy no longer says
             these rows cannot be moved (Jake, 2026-08-26) -- dragging
             one now works, and pins it to the home page. -->
        <p
          v-if="category.is_home && adoptedProductCount > 0"
          class="category-card__adopted-hint"
          data-testid="ct-categories-adopted-hint"
        >
          <span class="tf-icon">info</span>
          {{ adoptedProductCount }}
          {{ adoptedProductCount === 1 ? "product has" : "products have" }}
          no category, so
          {{ adoptedProductCount === 1 ? "it appears" : "they appear" }}
          here on the home page. Drag
          {{ adoptedProductCount === 1 ? "it" : "them" }} to set the order
          shoppers see, or assign a category in the Product editor to move
          {{ adoptedProductCount === 1 ? "it" : "them" }} elsewhere.
        </p>
        <div class="category-card__products" :data-testid="`ct-categories-products-${category.id}`">
          <div v-if="products.length === 0" class="category-card__products-empty">
            No products assigned yet. Open a product in the Product editor and add it to this category.
          </div>
          <div
            v-else
            ref="productsGrid"
            class="category-card__products-grid"
            :data-testid="`ct-categories-products-grid-${category.id}`"
          >
            <div
              v-for="product in products"
              :key="product.id"
              class="category-card__product-row"
              :class="{
                'category-card__product-row--hidden': !product.visible,
                'category-card__product-row--adopted': product.adopted,
              }"
              :data-testid="`ct-categories-product-${product.id}`"
              :data-product-id="product.id"
              :data-adopted="product.adopted ? 'true' : 'false'"
            >
              <!-- EVERY row is draggable, adopted or not (Jake,
                   2026-08-26). #3004 gave adopted rows a lock glyph
                   because the product-order endpoint rejected an id with
                   no membership row; that endpoint now MATERIALISES one
                   under Home, so the lock no longer describes anything
                   real. The "Uncategorised" pill below stays -- it is a
                   status, not a restriction. -->
              <span
                class="tf-icon category-card__product-drag"
                :data-testid="`ct-categories-product-drag-${product.id}`"
                title="Drag to reorder"
                aria-label="Drag to reorder"
                >drag_indicator</span
              >
              <product-thumb-hover-zoom
                class="category-card__product-thumb-wrap"
                :image-url="productThumbUrl(product)"
                :alt="product.name"
                testid-base="ct-categories-product-thumb"
                :placeholder-testid="`ct-categories-product-thumb-${product.id}`"
                :id="product.id"
              />
              <div class="category-card__product-meta">
                <div class="category-card__product-line1">
                  <router-link
                    :to="{ name: 'product_editor', params: { campaignProductId: String(product.id) } }"
                    class="category-card__product-name"
                    :data-testid="`ct-categories-product-name-link-${product.id}`"
                    title="Open in the Product editor"
                  >{{ product.name }}</router-link>
                  <span v-if="!product.visible" class="category-card__pill category-card__pill--hidden">Hidden</span>
                  <span
                    v-if="product.adopted"
                    class="category-card__pill category-card__pill--adopted"
                    :data-testid="`ct-categories-product-adopted-pill-${product.id}`"
                    >Uncategorised</span
                  >
                </div>
                <div class="category-card__product-line2">
                  <span class="category-card__product-sku">{{ product.sku }}</span>
                  <!-- The server's quote (min of price_preview), never the row's
                       legacy `price` (= default_sale_price, which disagreed
                       with the charge). Same source as the product card and
                       table (2026-09-29). -->
                  <span
                    class="category-card__product-price"
                    :data-testid="`ct-categories-product-price-${product.id}`"
                    :data-price-unavailable="productPriceLabel(product) === null ? 'true' : null"
                    >{{ productPriceLabel(product) ?? pricePlaceholder }}</span
                  >
                  <pricing-mode-badge
                    :mode="product.pricing_mode"
                    :testid="`ct-categories-product-pricing-mode-${product.id}`"
                  />
                  <button
                    v-if="!product.visible"
                    type="button"
                    class="category-card__product-copy-btn"
                    :data-testid="`ct-categories-product-copy-link-${product.id}`"
                    title="Copy shareable link"
                    @click="onCopyProductLink(product)"
                  >
                    <span class="tf-icon">content_copy</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Sortable } from "sortablejs";
import RichTextEditor from "@/components/RichTextEditor.vue";

import ImageUploader from "@/components/ImageUploader.vue";
import ProductThumbHoverZoom from "@/components/common/ProductThumbHoverZoom.vue";
import { buildImageUrl } from "@/utils/imageBaseUrl";
import { categoryBannerSizeLabel } from "@/utils/categoryBannerLayout";
import { CURRENCY_EMPTY_PLACEHOLDER } from "@/utils/currency";
import PricingModeBadge from "../StoreProducts/PricingModeBadge.vue";
import { fromPriceLabel } from "../StoreProducts/pricingMode";

export default {
  name: "CategoryCard",

  components: {
    PricingModeBadge,
    "rich-text-editor": RichTextEditor,
    "image-uploader": ImageUploader,
    "product-thumb-hover-zoom": ProductThumbHoverZoom,
  },

  props: {
    category: { type: Object, required: true },
    // The store's OWN slug (campaign.slug) -- the category path
    // nests under it: <storeBaseUrl>/shop/<storeSlug>/c/<category.slug>.
    storeSlug: { type: String, default: "" },
    storeBaseUrl: { type: String, default: "" },
    isNew: { type: Boolean, default: false },
    editMode: { type: Boolean, default: true },
    bannerUploading: { type: Boolean, default: false },
    // CC1 accordion (ADR 0059 2026-07-27 revision): whether THIS card is
    // the single open card. Owned by the parent -- this card only emits
    // toggle-expand and never flips its own state.
    expanded: { type: Boolean, default: false },
  },

  emits: [
    "update-name",
    "update-rich-text",
    "update-hidden",
    "update-banner",
    "delete-category",
    "update-product-order",
    "toggle-expand",
  ],

  data() {
    // The rich-text editor config now lives in the shared RichTextEditor.vue
    // wrapper (TipTap); the product-thumb hover-zoom now lives in the shared
    // ProductThumbHoverZoom.vue component. CategoryCard holds no local editor
    // or hover-zoom state anymore.
    return {};
  },

  computed: {
    bannerSizeLabel() {
      return categoryBannerSizeLabel();
    },

    pricePlaceholder() {
      return CURRENCY_EMPTY_PLACEHOLDER;
    },

    products() {
      return Array.isArray(this.category.products) ? this.category.products : [];
    },

    hiddenProductCount() {
      return this.category.hidden_product_count || 0;
    },

    // Products on this list ONLY because they have no category at all
    // (Home adopts them). Drives the explanatory hint below the grid.
    adoptedProductCount() {
      return this.products.filter((p) => p.adopted).length;
    },

    // Collapsed-header count chip (also shown expanded -- same chip).
    // CC1 (ADR 0059 2026-07-27 revision): collapsed header row shows
    // "N products - M hidden" ("1 product" singular; "No products" when
    // n is 0). No hidden segment when there are no hidden products.
    countLabel() {
      const n = this.products.length;
      if (n === 0) return "No products";
      const base = `${n} product${n === 1 ? "" : "s"}`;
      if (this.hiddenProductCount > 0) {
        return `${base} · ${this.hiddenProductCount} hidden`;
      }
      return base;
    },

    // Category is "unset" when it's a brand-new, not-yet-named card --
    // the slug can't be generated yet (Locked Decisions.md).
    isUnset() {
      return !this.category.is_home && this.isNew && !this.category.name;
    },

    categoryPath() {
      const slug = this.category.slug || "";
      return `/c/${slug}`;
    },

    // Full storefront path, mirrors StoreShopper.vue's baseUrl/shop-prefix
    // construction: <baseUrl>/shop/<storeSlug>/c/<categorySlug>. Home
    // Page has no slug -- its path IS the store root.
    storefrontUrl() {
      const base = (this.storeBaseUrl || "").replace(/\/+$/, "");
      const storePath = `/shop/${this.storeSlug || ""}`;
      if (this.category.is_home) {
        return `${base}${storePath}`;
      }
      return `${base}${storePath}${this.categoryPath}`;
    },

    emphUrlBar() {
      return !!this.category.hidden;
    },

    urlBarIcon() {
      if (this.category.is_home) return "home";
      return this.category.hidden ? "link" : "public";
    },

    urlBarLabel() {
      if (this.category.is_home) return "Storefront home";
      return this.category.hidden ? "Private link — nav-hidden" : "Storefront URL";
    },

    bannerUrl() {
      return this.category.banner_image ? buildImageUrl(this.category.banner_image) : null;
    },
  },

  methods: {
    // The server's quote for a product row, or null (placeholder).
    productPriceLabel(product) {
      return fromPriceLabel(product && product.price_preview);
    },

    // Product row thumbnail. Unlike `bannerUrl` (a real upload KEY that
    // needs the MinIO image-base prefix from buildImageUrl), the backend
    // now returns `product.thumbnail` as an ABSOLUTE url (picked from
    // campaign_products.storefront_images -- see store_categories.py
    // _serialize_category_with_products). Render it RAW: wrapping an
    // already-absolute url in buildImageUrl double-prefixes it (MinIO
    // base + the absolute url) and produces a broken image. Mirrors the
    // storefront's raw-render pattern for the same field (the storefront
    // Product page's hero image and the product-grid card thumbnails).
    productThumbUrl(product) {
      return product.thumbnail || null;
    },

    onNameBlur(event) {
      const value = (event.target.value || "").trim();
      if (!value) return;
      if (value === this.category.name) return;
      this.$emit("update-name", this.category.id, value);
    },

    onRichTextChange(html) {
      this.$emit("update-rich-text", this.category.id, html);
    },

    onToggleVisibility() {
      this.$emit("update-hidden", this.category.id, !this.category.hidden);
    },

    // CC1 accordion: this card never decides its own open state -- it
    // only asks the parent to toggle it. The parent (single openCategoryId)
    // decides whether that makes THIS card open and, if so, collapses
    // whatever else was open.
    onToggleExpand() {
      this.$emit("toggle-expand", this.category.id);
    },

    onBannerUpload(file) {
      this.$emit("update-banner", this.category.id, file);
    },

    onBannerRemove() {
      this.$emit("update-banner", this.category.id, null);
    },

    onBannerError(message) {
      if (this.$store && this.$store.dispatch) {
        this.$store.dispatch("raiseError", message);
      }
    },

    async onCopyLink() {
      await this._copyToClipboard(this.storefrontUrl);
    },

    // There is no storefront-facing per-product deep link route today
    // (no /store/:slug/p/:id) -- the honest shareable link for a hidden
    // product is the CATEGORY's storefront URL, the actual path a
    // shopper follows to reach it. Reuses the same category link the
    // card's own Copy link button emits.
    async onCopyProductLink(_product) {
      await this._copyToClipboard(this.storefrontUrl);
    },

    async _copyToClipboard(url) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
        } else {
          const textarea = document.createElement("textarea");
          textarea.value = url;
          textarea.style.position = "fixed";
          textarea.style.opacity = "0";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);
        }
        if (this.$store && this.$store.dispatch) {
          this.$store.dispatch("raiseSuccess", "Link copied to clipboard");
        }
      } catch (err) {
        if (this.$store && this.$store.dispatch) {
          this.$store.dispatch("raiseError", "Failed to copy link");
        }
      }
    },

    // -----------------------------------------------------------------
    // Product drag reorder (C3) -- membership NEVER mutated here. This
    // handler is what sortablejs' onEnd calls with the DOM drag's
    // old/new index over the rendered `products` array (already in
    // sort_order). Splice semantics (remove at oldIndex, insert at
    // newIndex) mirror StorefrontListingPanel.vue's onReorder -- the
    // single write model is "recompute the whole order, emit it, let the
    // parent PATCH it." A no-op drag (same index) never emits, so an
    // accidental non-move drag can't dirty an autosave indicator.
    // -----------------------------------------------------------------
    onProductReorder(oldIndex, newIndex) {
      if (oldIndex === newIndex) return;

      // Reorder over the FULL rendered list and emit EVERY id in it,
      // adopted rows included (Jake, 2026-08-26).
      //
      // #3004 dropped adopted ids here because the product-order
      // endpoint rejected the whole request for any id with no
      // membership row. That endpoint now MATERIALISES a membership row
      // under Home for a product that is uncategorized in this store
      // (store_categories.py::patch_category_product_order), so the ids
      // are accepted and the drag persists.
      //
      // Emitting the full list is not just "now allowed", it is
      // REQUIRED: sort_order is positional, so omitting the adopted rows
      // would renumber the members around products the server still
      // lists on this card, and the rendered order would stop matching
      // the persisted one.
      const reordered = this.products.slice();
      const [moved] = reordered.splice(oldIndex, 1);
      if (moved === undefined) return;
      reordered.splice(newIndex, 0, moved);

      const orderedIds = reordered.map((p) => p.id);
      if (orderedIds.length === 0) return;

      this.$emit("update-product-order", this.category.id, orderedIds);
    },

    _destroySortable() {
      if (this._sortableInstance) {
        this._sortableInstance.destroy();
        this._sortableInstance = null;
      }
    },

    // Whole-row dragging (Jake, 2026-08-04): earlier this used sortablejs'
    // `handle` to restrict dragging to the drag-indicator icon only. That
    // made the row hard to grab. `filter` inverts the contract -- the row
    // is draggable everywhere EXCEPT the listed interactive elements (the
    // name link, the thumbnail incl. its hover-zoom wrapper, and the
    // hidden-product copy-link button), so those keep their own click
    // behavior (navigate / copy) instead of starting a drag. The thumbnail
    // is now the shared ProductThumbHoverZoom component; the
    // `category-card__product-thumb-wrap` class is passed through onto its
    // root element (image AND placeholder) so this selector still excludes
    // the whole thumbnail from drag-start.
    // `preventOnFilter: false` is required for that -- sortablejs' default
    // (true) calls preventDefault() on the initiating pointer event for
    // any filtered target, which would swallow the router-link navigation
    // and the copy-link button's click.
    _initSortable() {
      this._destroySortable();
      const el = this.$refs.productsGrid;
      if (!el) return;
      this._sortableInstance = Sortable.create(el, {
        animation: 150,
        // `.category-card__product-row--adopted` was in this list under
        // #3004 so an adopted row could not START a drag. It came out
        // with the matching guard in onProductReorder (Jake,
        // 2026-08-26) -- both had to go together, because `filter` only
        // blocks drag START while onProductReorder also covered a row
        // being DISPLACED by someone else's drag. Removing one and not
        // the other yields a half-working drag, which is worse than the
        // lock it replaced.
        //
        // What remains excluded is unchanged: the interactive elements
        // that own their own click (name link, thumbnail incl. its
        // hover-zoom wrapper, hidden-product copy button).
        filter:
          ".category-card__product-name, .category-card__product-thumb-wrap, .category-card__product-copy-btn",
        preventOnFilter: false,
        // The shared ProductThumbHoverZoom owns its own hover state and
        // hides the overlay on scroll (its capture-phase listener) and on
        // mouseleave, so no drag-start hover-teardown is needed here -- a
        // drag beginning anywhere on the row moves the pointer off the
        // thumb (mouseleave) and any scroll during the drag hides it too.
        onEnd: ({ oldIndex, newIndex }) => {
          this.onProductReorder(oldIndex, newIndex);
        },
      });
    },
  },

  watch: {
    // The grid only exists in the DOM while the category has products
    // (v-if in the template) -- (re)bind Sortable whenever that flips,
    // and whenever the product SET changes shape (a reorder re-renders
    // from the parent's authoritative order, so the DOM and the v-for
    // stay in sync; Sortable tracks the same container element and does
    // not need re-init for that case, matching the established
    // StorefrontListingPanel.vue pattern).
    "products.length": {
      handler() {
        this.$nextTick(() => {
          if (this.products.length > 0) {
            this._initSortable();
          } else {
            this._destroySortable();
          }
        });
      },
      immediate: true,
    },
  },

  mounted() {
    // C4 polish (Locked Decisions.md "Add category" -> "name field
    // focused"): a brand-new, not-yet-named card should drop the user
    // straight into the name field instead of making them hunt for it.
    // Guarded to isNew + no name yet so re-mounts of an already-named
    // (but still "New -- unsaved" pending its first PATCH round-trip)
    // card never steal focus back from whatever the user is doing next.
    if (this.isNew && !this.category.name && this.$refs.nameInput) {
      this.$refs.nameInput.focus();
    }
  },

  beforeUnmount() {
    this._destroySortable();
  },
};
</script>

<style scoped>
.category-card {
  background: var(--tf-bg, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-lg, 12px);
  box-shadow: 0 1px 2px rgba(20, 40, 60, 0.05);
}

.category-card--new {
  border-color: var(--tf-primary, #119ae9);
  box-shadow: 0 0 0 3px var(--tf-primary-soft, #e3f2fd);
}

.category-card--hidden {
  border-left: 3px solid #8a6d3b;
}

.category-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-top-left-radius: 11px;
  border-top-right-radius: 11px;
  cursor: pointer;
}

.category-card__header:focus-visible {
  outline: 2px solid var(--tf-primary, #119ae9);
  outline-offset: -2px;
}

/* The bottom border only reads as a header/body divider while the body
   is actually expanded -- a class toggle (bound from `expanded`, not a
   CSS-only selector) so it never depends on how v-show serializes its
   inline style. */
.category-card__header--expanded {
  border-bottom: 1px solid var(--tf-border, #e0e0e0);
}

.category-card--hidden .category-card__header {
  background: #fdf3e2;
}

.category-card__pin {
  color: var(--tf-fg-4, #757575);
  font-size: 20px;
}

.category-card__drag {
  color: var(--tf-fg-4, #757575);
  font-size: 20px;
  cursor: grab;
}

.category-card__heading {
  min-width: 0;
}

.category-card__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.category-card__title {
  font-family: "Poppins", sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: var(--tf-secondary, #133752);
  letter-spacing: -0.2px;
}

.category-card__subtext {
  font-size: 12.5px;
  color: var(--tf-fg-4, #757575);
  margin-top: 2px;
}

.category-card__spacer {
  flex: 1;
}

.category-card__pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #0a6cb0;
  background: var(--tf-primary-soft, #e3f2fd);
  padding: 3px 9px;
  border-radius: 9999px;
}

.category-card__pill .tf-icon {
  font-size: 13px;
}

.category-card__pill--hidden {
  color: #8a6d3b;
  background: #fdf3e2;
}

.category-card__pill--new {
  color: var(--tf-success-dark, #2e7d32);
  background: #e6faf2;
}

/* An ADOPTED product sits on the Home list only because it has no
   category. Neutral grey, not a warning colour -- it is a normal state
   the builder may simply not have gotten to yet, not an error. */
.category-card__pill--adopted {
  color: var(--tf-fg-3, #5f6368);
  background: var(--tf-bg-3, #eceff1);
}

.category-card__count-chip {
  font-size: 12px;
  font-weight: 500;
  color: #0a6cb0;
  background: var(--tf-primary-soft, #e3f2fd);
  padding: 4px 10px;
  border-radius: 9999px;
  white-space: nowrap;
}

.category-card__visibility-toggle,
.category-card__delete-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid var(--tf-border, #e0e0e0);
  color: var(--tf-fg-3, #616161);
  border-radius: 9999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.category-card__delete-btn {
  color: var(--tf-red, #ef0405);
}

.category-card__visibility-toggle--hidden {
  color: #8a6d3b;
  background: #fdf3e2;
  border-color: #eccf9a;
}

.category-card__expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: var(--tf-fg-4, #757575);
  border-radius: 9999px;
  padding: 6px;
  cursor: pointer;
  flex-shrink: 0;
}

.category-card__expand-btn .tf-icon {
  font-size: 22px;
}

.category-card__body {
  padding: 18px 22px 22px;
}

/* Wraps the Banner ImageUploader so its dropzone reads as a compact
   strip inside the card rather than the taller default used on
   full-page uploaders (Branding/Email) -- CC1 "tighten the banner
   drop-zone vertically" (Locked Decisions.md). Overrides the shared
   component's own padding/min-height via :deep() rather than forking
   ImageUploader.vue, which stays shared with Branding/Email. */
.category-card__banner-tight :deep(.image-uploader__dropzone) {
  padding: var(--tf-sp-3, 12px) var(--tf-sp-4, 16px);
  gap: 4px;
}

.category-card__banner-tight :deep(.image-uploader__icon) {
  font-size: 20px;
}

.category-card__banner-tight :deep(.image-uploader__banner) {
  min-height: 64px;
}

.category-card__banner-tight :deep(.image-uploader__img) {
  max-height: 80px;
}

.category-card__section {
  margin-top: 22px;
}

.category-card__section:first-child {
  margin-top: 0;
}

.category-card__banner-guidance {
  font-size: 13px;
  color: var(--tf-text-muted, var(--tf-fg-4));
  margin: 0 0 10px;
}

.category-card__section-label {
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: var(--tf-fg-4, #757575);
  margin-bottom: 10px;
}

.category-card__field-label {
  font-size: 12px;
  color: var(--tf-primary, #119ae9);
  margin-bottom: 6px;
  display: block;
}

.category-card__name-input {
  width: 100%;
  border: none;
  border-bottom: 1px solid var(--tf-fg-5, #9e9e9e);
  padding: 6px 0;
  font-size: 18px;
  font-family: "Poppins", sans-serif;
  color: var(--tf-fg-1, #212121);
  background: transparent;
}

.category-card__name-input:focus {
  outline: none;
  border-bottom-color: var(--tf-primary, #119ae9);
}

.category-card__hint {
  font-size: 12px;
  color: var(--tf-fg-4, #757575);
  margin-top: 6px;
}

.category-card__urlbar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid var(--tf-border, #e0e0e0);
  background: #fafbfc;
}

.category-card__urlbar--hidden {
  border-color: #eccf9a;
  background: #fdf3e2;
}

.category-card__urlbar-icon {
  font-size: 18px;
  color: var(--tf-fg-4, #757575);
}

.category-card__urlbar--hidden .category-card__urlbar-icon {
  color: #8a6d3b;
}

.category-card__urlbar-body {
  flex: 1;
  min-width: 0;
}

.category-card__urlbar-label {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: var(--tf-fg-4, #757575);
  margin-bottom: 2px;
}

.category-card__urlbar--hidden .category-card__urlbar-label {
  color: #8a6d3b;
}

.category-card__urlbar-unset {
  font-size: 13px;
  color: var(--tf-fg-4);
  font-style: italic;
}

.category-card__urlbar-path {
  display: block;
  font-size: 13px;
  color: var(--tf-fg-2, #424242);
  font-family: ui-monospace, Menlo, monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
}

/* Reads as a link (subtle underline on hover) without disrupting the
   monospace urlbar layout -- Jake wants it clickable, not styled like a
   loud hyperlink. */
a.category-card__urlbar-path:hover {
  color: var(--tf-primary, #119ae9);
  text-decoration: underline;
}

.category-card__urlbar-lock {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--tf-fg-4);
  white-space: nowrap;
}

.category-card__urlbar-lock .tf-icon {
  font-size: 13px;
}

.category-card__copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid var(--tf-border, #e0e0e0);
  color: var(--tf-primary, #119ae9);
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
}

.category-card__urlbar--hidden .category-card__copy-btn {
  border-color: #eccf9a;
  color: #8a6d3b;
}

.category-card__rte :deep(.ck-editor__editable) {
  min-height: 96px;
}

.category-card__products-header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.category-card__products-count {
  font-size: 12px;
  color: var(--tf-fg-4);
}

.category-card__lock-hint {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--tf-fg-4, #757575);
}

.category-card__lock-hint .tf-icon {
  font-size: 14px;
}

.category-card__drag-hint {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12.5px;
  color: var(--tf-fg-4, #757575);
  margin: 0 0 12px;
}

.category-card__drag-hint .tf-icon {
  font-size: 14px;
}

.category-card__adopted-hint {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  color: var(--tf-fg-4, #757575);
  margin: 0 0 12px;
}

.category-card__adopted-hint .tf-icon {
  font-size: 14px;
}

.category-card__products-empty {
  border: 1px dashed var(--tf-border, #e0e0e0);
  border-radius: 8px;
  padding: 18px 16px;
  text-align: center;
  font-size: 13.5px;
  color: var(--tf-fg-4, #757575);
  background: #fafbfc;
}

.category-card__products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 8px 16px;
}

.category-card__product-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 8px;
  border: 1px solid var(--tf-border, #e0e0e0);
  background: #fff;
  min-width: 0;
}

/* Two-line text column: name on line 1, SKU + price (+ copy) on line 2.
   Stacking these vertically lets the row need less horizontal width so
   the responsive grid fits more columns and the larger thumbnail. */
.category-card__product-meta {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.category-card__product-line1,
.category-card__product-line2 {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.category-card__product-drag {
  color: var(--tf-fg-4, #757575);
  font-size: 18px;
  cursor: grab;
  flex-shrink: 0;
}

/* Adopted rows are draggable like any other (Jake, 2026-08-26), so they
   keep the ordinary drag handle and its grab cursor. The dashed border
   stays as a STATUS cue -- it pairs with the "Uncategorised" pill to say
   "this has no category yet", which is still true until it is dragged.
   The former `.category-card__product-drag--locked` rule went with the
   lock glyph it styled. */
.category-card__product-row--adopted {
  border-style: dashed;
}

.category-card__product-copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #eccf9a;
  color: #8a6d3b;
  border-radius: 7px;
  padding: 6px;
  cursor: pointer;
  flex-shrink: 0;
}

.category-card__product-copy-btn .tf-icon {
  font-size: 15px;
}

.category-card__product-row--hidden {
  border-color: #eccf9a;
  background: #fdf3e2;
}

/* The product-row thumbnail + hover-zoom is the shared
   ProductThumbHoverZoom component (frontend/src/components/common/). This
   card passes the `category-card__product-thumb-wrap` class through onto
   its root so the sortablejs drag `filter` (see _initSortable) still
   excludes the thumbnail from starting a drag; all thumb/overlay sizing
   and the viewport-clamp math live in the shared component. */
.category-card__product-name {
  display: block;
  flex: 1;
  min-width: 0;
  font-size: 13.5px;
  color: var(--tf-fg-1, #212121);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: none;
}

.category-card__product-name:hover {
  color: var(--tf-primary, #119ae9);
  text-decoration: underline;
}

.category-card__product-sku {
  font-size: 11.5px;
  color: var(--tf-fg-4, #757575);
  font-family: ui-monospace, Menlo, monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.category-card__product-price {
  font-size: 13px;
  color: var(--tf-fg-3, #616161);
  font-weight: 500;
  flex-shrink: 0;
  margin-left: auto;
}
</style>
